/**
 * Union grouping and spouse rails — docs/08 §4.2 step 2, §4.3.
 *
 * A monogamous pair is one group, `[A ⚭ B]`. A person with several unions —
 * concurrent or sequential, which the model treats identically — becomes the
 * **hub** of a rail: the hub in the middle, spouses ordered by union start
 * (nulls last) alternating right then left, so `W2 ═ H ═ W1 ═ … ═ W3`.
 *
 * The alternation is not decoration. Putting every spouse on one side pushes the
 * hub to the edge of its own household and drags the children's drop lines
 * across each other; flanking keeps the hub centred over the family it anchors,
 * which is the only arrangement that stays readable at four wives.
 */

import type { TreeEdge } from '@famtree/shared';
import { fuzzyDateSortKey } from '@famtree/shared';

export interface CoupleGroup {
  /** Person ids in the order they sit on the rail, left to right (logical). */
  members: string[];
  /** The person every union in this group runs through, if there is one. */
  hubId: string | null;
  generation: number;
  unionIds: string[];
}

export function buildCoupleGroups(
  personIds: string[],
  edges: TreeEdge[],
  generation: Map<string, number>,
): CoupleGroup[] {
  const present = new Set(personIds);
  const unions = edges.filter(
    (edge) => edge.kind === 'union' && present.has(edge.a) && present.has(edge.b),
  );

  const partnersOf = new Map<string, TreeEdge[]>();
  for (const union of unions) {
    for (const person of [union.a, union.b]) {
      partnersOf.set(person, [...(partnersOf.get(person) ?? []), union]);
    }
  }

  // Connected components over union edges: a chain of remarriages (A married B,
  // B later married C) is one rail, not two overlapping ones.
  const seen = new Set<string>();
  const groups: CoupleGroup[] = [];

  for (const personId of personIds) {
    if (seen.has(personId)) continue;
    const component: string[] = [];
    const componentUnions = new Set<TreeEdge>();
    const stack = [personId];
    seen.add(personId);
    while (stack.length > 0) {
      const node = stack.pop()!;
      component.push(node);
      for (const union of partnersOf.get(node) ?? []) {
        componentUnions.add(union);
        const other = union.a === node ? union.b : union.a;
        if (!seen.has(other)) {
          seen.add(other);
          stack.push(other);
        }
      }
    }

    if (component.length === 1) {
      groups.push({
        members: component,
        hubId: null,
        generation: generation.get(personId) ?? 0,
        unionIds: [],
      });
      continue;
    }

    // The hub is whoever holds the most unions in this component; ties break on
    // id so the same family always draws the same way.
    const hubId = [...component].sort((x, y) => {
      const byCount = (partnersOf.get(y)?.length ?? 0) - (partnersOf.get(x)?.length ?? 0);
      return byCount !== 0 ? byCount : x.localeCompare(y);
    })[0]!;

    const hubUnions = [...componentUnions]
      .filter((union) => union.a === hubId || union.b === hubId)
      .sort(byStart);
    const spouses = hubUnions.map((union) => (union.a === hubId ? union.b : union.a));

    // Alternate: first spouse to the hub's right, second to the left, and so on.
    const right: string[] = [];
    const left: string[] = [];
    spouses.forEach((spouse, index) => (index % 2 === 0 ? right : left).push(spouse));

    // Anyone in the component who is not the hub's own spouse (a spouse's other
    // spouse, further along a remarriage chain) trails on the right, so the rail
    // reads outward from the hub in both directions.
    const placed = new Set([hubId, ...spouses]);
    const trailing = component.filter((id) => !placed.has(id)).sort((x, y) => x.localeCompare(y));

    groups.push({
      members: [...left.reverse(), hubId, ...right, ...trailing],
      hubId,
      generation: generation.get(hubId) ?? 0,
      unionIds: [...componentUnions].map((union) => union.id),
    });
  }

  return groups;
}

/** Union order on a rail: by start date, nulls last, then by id for determinism. */
function byStart(x: TreeEdge, y: TreeEdge): number {
  const kx = fuzzyDateSortKey(x.started);
  const ky = fuzzyDateSortKey(y.started);
  if (kx === ky) return x.id.localeCompare(y.id);
  if (kx == null) return 1;
  if (ky == null) return -1;
  return kx - ky;
}
